import get from 'lodash/get'
import { isEmpty } from 'lodash-es'
import { Dispatch } from 'redux'

import {
  FavoritedProviderIdRecordsResponse,
  FavoritedProvidersSaveEventPayload,
} from '@lyrahealth-inc/shared-app-logic'

import {
  clearFavoritedProvidersInProviderIdListForUser,
  getAllProvidersFromFavoritedProvidersList,
  getFavoritedProvidersForUser,
  saveFavoritedProvidersEvent,
} from '../../../../common/http/data/onboard'
import { SET_HAS_PAST_FAVORITED_PROVIDERS } from '../../../../data/user/userActions'

export const SET_FAVORITED_PROVIDERS_IDS_FOR_USER = 'SET_FAVORITED_PROVIDERS_IDS_FOR_USER'
export const FAVORITE_NEW_PROVIDER = 'FAVORITE_NEW_PROVIDER'
export const REMOVE_FAVORITED_PROVIDER = 'REMOVE_FAVORITED_PROVIDER'
export const SET_FAVORITED_PROVIDERS_METADATA_MAP = 'SET_FAVORITED_PROVIDERS_METADATA_MAP'
export const CLEAR_FAVORITED_PROVIDERS_FOR_USER = 'CLEAR_FAVORITED_PROVIDERS_FOR_USER'
export const REMOVE_MULTIPLE_FAVORITED_PROVIDERS = 'REMOVE_MULTIPLE_FAVORITED_PROVIDERS'

// Fetches the list of provider records for the user's currently favorited providers
// Returns metadata related to each favoriting event such as date, treatmentPreference, and the providers ID
export const fetchFavoritedProvidersRecordsForUser = ({ userId }: { userId: string }) => {
  return async (dispatch: $TSFixMe) => {
    let responseData: FavoritedProviderIdRecordsResponse[] = []
    try {
      const response = await getFavoritedProvidersForUser(userId)
      const data = get(response, 'data')
      responseData = isEmpty(data) ? [] : data
      // Create array of provider IDs
      const providerIdsList = responseData.map((item) => item.providerLyraId)

      // Create fetched records metadata map. The key is the provider ID while the value is an object
      // containing the metadata for that favorited event (treatmentPreference, timestamp, search IDs, etc.)
      const metadataMap = responseData.reduce((acc, item) => {
        const { providerLyraId, ...metadata } = item
        return {
          ...acc,
          [providerLyraId]: metadata,
        }
      }, {})

      dispatch({
        type: SET_FAVORITED_PROVIDERS_IDS_FOR_USER,
        payload: providerIdsList,
      })

      dispatch({
        type: SET_FAVORITED_PROVIDERS_METADATA_MAP,
        payload: metadataMap,
      })
    } catch (error) {
      // If it fails, initialize both stores to empty
      console.error(error)
      dispatch({
        type: SET_FAVORITED_PROVIDERS_IDS_FOR_USER,
        payload: [],
      })
      dispatch({
        type: SET_FAVORITED_PROVIDERS_METADATA_MAP,
        payload: {},
      })
    }
  }
}

export const saveFavoritedProvider = ({
  userId,
  data,
}: {
  userId: string
  data: FavoritedProvidersSaveEventPayload
}) => {
  return (dispatch: Dispatch<any>) => {
    const { providerId } = data
    dispatch(addProviderToFavoritedProviders({ providerId, data }))

    // Set hasPastFavoritedProviders to true when favoriting a provider
    dispatch({
      type: SET_HAS_PAST_FAVORITED_PROVIDERS,
    })

    const request = saveFavoritedProvidersEvent({ userId, data })
    return new Promise(function (resolve, reject) {
      return request.then(
        (submissionReturn) => {
          return resolve(submissionReturn)
        },
        (submissionError) => {
          // TODO - add failure error banner
          // If it fails we should remove the provider from the local list
          dispatch(removeProviderFromFavoritedProviders({ providerId }))
          return reject(submissionError)
        },
      )
    })
  }
}

export const saveUnfavoritedProvider = ({
  userId,
  data,
}: {
  userId: string
  data: FavoritedProvidersSaveEventPayload
}) => {
  return (dispatch: Dispatch) => {
    const { providerId } = data
    dispatch(removeProviderFromFavoritedProviders({ providerId }))

    const request = saveFavoritedProvidersEvent({ userId, data })
    return new Promise(function (resolve, reject) {
      return request.then(
        (submissionReturn) => {
          return resolve(submissionReturn)
        },
        (submissionError) => {
          // TODO - add failure error banner
          // if it fails we should add the provider back to the local list
          dispatch(addProviderToFavoritedProviders({ providerId, data }))
          return reject(submissionError)
        },
      )
    })
  }
}

// Fetches the list of provider profiles that are actively favorited by the current user
export const fetchProviderProfilesFromFavoritedProvidersIds = ({
  userId,
  providerIds,
}: {
  userId: string
  providerIds: string[]
}) => {
  return (dispatch: Dispatch) => {
    if (providerIds.length === 0) {
      return Promise.resolve([])
    }
    const request = getAllProvidersFromFavoritedProvidersList(userId, providerIds)
    return new Promise((resolve, reject) => {
      return request.then(
        (response) => {
          resolve(response.data || [])
        },
        (error) => {
          reject(error)
        },
      )
    })
  }
}

export const addProviderToFavoritedProviders = ({
  providerId,
  data,
}: {
  providerId: string
  data: FavoritedProvidersSaveEventPayload
}) => {
  return {
    type: FAVORITE_NEW_PROVIDER,
    payload: { providerId, data },
  }
}

export const clearFavoritedProvidersForUser = ({
  userId,
  providerIds,
  isClearAll = false,
}: {
  userId: string
  providerIds: string[]
  isClearAll?: boolean
}) => {
  return (dispatch: Dispatch) => {
    const request = clearFavoritedProvidersInProviderIdListForUser(userId, providerIds)
    return new Promise((resolve, reject) => {
      return request.then(
        (response) => {
          if (isClearAll) {
            dispatch({
              type: CLEAR_FAVORITED_PROVIDERS_FOR_USER,
            })
          } else {
            dispatch({
              type: REMOVE_MULTIPLE_FAVORITED_PROVIDERS,
              payload: { providerIds },
            })
          }
          resolve(response)
        },
        (error) => {
          reject(error)
        },
      )
    })
  }
}

export const removeProviderFromFavoritedProviders = ({ providerId }: { providerId: string }) => {
  return {
    type: REMOVE_FAVORITED_PROVIDER,
    payload: providerId,
  }
}
